import { useRef, useEffect, useLayoutEffect, useContext, useCallback } from 'react'
import classNames from 'classnames'
import { createUseStyles } from 'react-jss'
import { useSelector, shallowEqual } from 'react-redux'
import gsap from 'gsap'
import { useInView } from 'react-intersection-observer'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import { Context as LoadingContext } from '@/context/loading'
import { useRaf, useResize } from '@/components/Handlers'
import { newMapRange } from '@/utils/math'
import style from './style'

const useStyles = createUseStyles(style)

const ImageZoom = ({
  img,
  scaleImgOnScroll,
  width,
  height,
  aboveTheFold,
}) => {
  const classes = useStyles(style)
  const $root = useRef()
  const $wrapper = useRef()
  const $img = useRef()
  const boundingRef = useRef()
  const { scroll } = useContext(ScrollbarContext)
  const { ready } = useContext(LoadingContext)
  const valRef = useRef()
  const [inViewRef, inView] = useInView({ triggerOnce: false })
  const { mediaQueryKey } = useSelector((state) => ({
    mediaQueryKey: state.mediaQuery.key,
  }), shallowEqual)

  const setRefs = useCallback((node) => {
    $root.current = node
    inViewRef(node)
  }, [inViewRef])

  const calculateBound = () => {
    boundingRef.current = {
      top: $root.current.getBoundingClientRect().top + (scroll.current.y || 0),
      height: $root.current.getBoundingClientRect().height,
    }
  }

  useRaf(() => {
    if (mediaQueryKey > 1) {
      if (scaleImgOnScroll && inView) {
        valRef.current = newMapRange(boundingRef.current.top - (scroll.current.y || 0), [window.innerHeight, -1 * boundingRef.current.height], [1.14, 1], true)
        if ($img.current) $img.current.style.transform = `scale3d(${valRef.current}, ${valRef.current}, 1)`
      }
    }
  }, [mediaQueryKey, inView])

  useResize(() => {
    calculateBound()
  })

  useEffect(() => {
    if (ready && aboveTheFold) {
      setTimeout(() => {
        calculateBound()
      }, 2000)
    }
  }, [ready])

  useEffect(() => {
    gsap.set($wrapper.current, { y: '150%', scaleY: 1.5 })
  }, [])

  useLayoutEffect(() => {
    if (inView) {
      gsap.to($wrapper.current, {
        y: '0%',
        ease: 'power1.out',
        delay: 0.3,
        duration: 1,
        scaleY: 1,
      })
    }
  }, [inView])

  return (
    <div
      ref={setRefs}
      className={classNames({
        [classes.root]: true,
        imageZoom: true,
      })}
    >
      <div
        ref={$wrapper}
        className={classes.wrapper}
      >
        <div
          ref={$img}
          className={`${classes.img} imageZoom__img`}
          style={{
            // backgroundImage: `url(${img})`,
            paddingBottom: `${(height / width) * 100}%`,
          }}
        >
          <img // eslint-disable-line
            src={img}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  )
}

ImageZoom.defaultProps = {
  inview: true,
  enterImg: false,
  aboveTheFold: false,
}

export default ImageZoom
